import { ActionReducerMapBuilder, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { RootState } from "../../../../../app/store";

import {
    createSCNReportLegacyThunk,
    createSCNReportThunk,
    fetchSCNReportsLegacyThunk,
    fetchSCNReportsThunk,
    fetchSinglePartySCNReportLegacyThunk,
    fetchSinglePartySCNReportThunk,
    upsertSCNReportLegacyThunk,
    upsertSCNReportThunk,
} from "./scnReportsThunk";

import { ReportStatus, SCNReport } from "../../../../../api/scnReports";

export interface SCNReportsState {
    scn_reports: EntityState<SCNReport, string>;
    status: "idle" | "loading" | "succeeded" | "saving" | "failed";
    error: string | null;
    report_status: ReportStatus | null;
}

const scnAdapter = createEntityAdapter<SCNReport, string>({
    selectId: (report) => report.party_id,
});

const initialState: SCNReportsState = {
    scn_reports: scnAdapter.getInitialState(),
    status: "idle",
    error: null,
    report_status: null,
};

const addFetchCases = (
    builder: ActionReducerMapBuilder<SCNReportsState>,
    thunk: typeof fetchSCNReportsLegacyThunk
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "loading";
            state.error = null;
            scnAdapter.removeAll(state.scn_reports);
        })
        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = action.payload || "Failed to fetch SCN Reports";
        })
        .addCase(thunk.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.error = null;
            scnAdapter.setAll(state.scn_reports, action.payload.scn_reports);
        });
};

const addCreateCases = (
    builder: ActionReducerMapBuilder<SCNReportsState>,
    thunk: typeof createSCNReportLegacyThunk
) => {
    builder
        .addCase(thunk.pending, (state, { meta }) => {
            const { party_id } = meta.arg.request;
            scnAdapter.upsertOne(state.scn_reports, {
                report_id: party_id,
                party_id,
                status: ReportStatus.SUBMITTED,
                last_updated_at: new Date().toISOString(),
            });
        })
        .addCase(thunk.rejected, (state, { meta }) => {
            const { party_id } = meta.arg.request;
            scnAdapter.updateOne(state.scn_reports, {
                id: party_id,
                changes: { status: ReportStatus.FAILED },
            });
        })
        .addCase(thunk.fulfilled, (state, action) => {
            const { party_id } = action.meta.arg.request;
            scnAdapter.updateOne(state.scn_reports, {
                id: party_id,
                changes: {
                    status: ReportStatus.IN_PROGRESS,
                    last_updated_at: new Date().toISOString(),
                },
            });
        });
};

const addUpdateCases = (
    builder: ActionReducerMapBuilder<SCNReportsState>,
    thunk: typeof upsertSCNReportLegacyThunk | typeof upsertSCNReportThunk
) => {
    builder
        .addCase(thunk.pending, (state) => {
            state.status = "saving";
            state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = action.payload || "Failed to update SCN Report";
        })
        .addCase(thunk.fulfilled, (state, action: any) => {
            const { request } = action.meta.arg;
            const id = request.party_id ?? action.meta.arg.report_id;

            scnAdapter.updateOne(state.scn_reports, {
                id,
                changes: {
                    content: request.content,
                    status: ReportStatus.AVAILABLE,
                    last_updated_at: new Date().toISOString(),
                },
            });

            state.status = "succeeded";
            state.error = null;
        });
};

const addFetchSinglePartyCases = (
    builder: ActionReducerMapBuilder<SCNReportsState>,
    thunk: typeof fetchSinglePartySCNReportLegacyThunk
) => {
    builder
        .addCase(thunk.pending, (state, { meta }) => {
            scnAdapter.updateOne(state.scn_reports, {
                id: meta.arg.report_id,
                changes: { status: ReportStatus.IN_PROGRESS },
            });
        })
        .addCase(thunk.rejected, (state, { meta }) => {
            scnAdapter.updateOne(state.scn_reports, {
                id: meta.arg.report_id,
                changes: { status: ReportStatus.FAILED },
            });
        })
        .addCase(thunk.fulfilled, (state, action) => {
            state.report_status = ReportStatus.AVAILABLE;
            scnAdapter.upsertOne(state.scn_reports, action.payload.scn_reports[0]);
        });
};

const scnSlice = createSlice({
    name: "scn",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // V1
        addFetchCases(builder, fetchSCNReportsLegacyThunk);
        addCreateCases(builder, createSCNReportLegacyThunk);
        addUpdateCases(builder, upsertSCNReportLegacyThunk);
        addFetchSinglePartyCases(builder, fetchSinglePartySCNReportLegacyThunk);

        // V2
        addFetchCases(builder, fetchSCNReportsThunk);
        addCreateCases(builder, createSCNReportThunk);
        addUpdateCases(builder, upsertSCNReportThunk);
        addFetchSinglePartyCases(builder, fetchSinglePartySCNReportThunk);
    },
});

export const scnReportsReducer = scnSlice.reducer;

const selectSCNState = (state: RootState): SCNReportsState => state.scnReports;

const scnSelectors = scnAdapter.getSelectors<RootState>(
    (state) => selectSCNState(state).scn_reports,
);

export const { selectEntities: selectSCNReportEntities } = scnSelectors;

export const selectSCNStatus = (state: RootState) => selectSCNState(state).status;

export const selectSCNReportStatus = (state: RootState) => selectSCNState(state).report_status;

export const selectSCNReportError = (state: RootState) => selectSCNState(state).error;